import {
	type FieldKey,
	type FieldType,
	type FilterValue,
	type MetricOrdering,
	type MetricParameters,
	type PresetSaveRequest,
	type PresetUpdateRequest,
	type ThresholdRequest,
	type UserPortfolioColumnPreferencePreferencesTypeEnum,
	InvestmentsBulkAnalysisControllerV2ApiFactory,
	PortfolioStudioPreferencesApiFactory,
	RiskModelControllerV1ApiFactory,
} from "@eu.mdotm.api/sphere";
import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import type { ColumnMetadata } from "$root/ui-lib/table/extra/ColumnChooser";
import { days, minutes } from "$root/utils";
import { t } from "i18next";
import type { PortfolioInsightPanelPreset } from "../portfolio-studio/insightPanelSession";

const lists = {
	presets: "presets",
};

const { newQuery, newAction, invalidateEntity, invalidate, invalidateLists } = repositoryHelperFactory("bulkAnalysis", {
	listKeys: Object.values(lists),
});

const preferencesApi = () => getApiGen(PortfolioStudioPreferencesApiFactory);
const bulkApi = () => getApiGen(InvestmentsBulkAnalysisControllerV2ApiFactory);
const riskApi = () => getApiGen(RiskModelControllerV1ApiFactory);

export const bulkAnalysisRepository = {
	invalidateEntity,
	invalidateLists,

	columnPreferences: newQuery(
		"columnPreferences",
		async () => {
			const preferences = await axiosExtract(preferencesApi().getUserPortfolioColumnMetricsOrderingPreferences());
			return (preferences.userPortfolioColumnPreferences ?? [])
				.filter(
					(x) => x.preferencesType !== "AVERAGE_SCORE", // remove once be has done migration
				)
				.map(
					(c): ColumnMetadata<UserPortfolioColumnPreferencePreferencesTypeEnum> => ({
						label: c.preferencesType ? t(`TABLE.HEADERS.${c.preferencesType}`) : "-",
						id: c.preferencesType!,
						checked: c.enabled ?? false,
						disabled: c.preferencesType === "NAME",
						hidden: c.preferencesType === "NAME",
					}),
				);
		},
		{
			staleTime: 0,
			cacheTime: minutes(10),
		},
	),

	//@deprecated not used anymore -> migrated to userStorage
	// updateColumns: newAction(
	// 	async (data: Array<SaveColumnMetadata<UserPortfolioColumnPreferencePreferencesTypeEnum>>) => {
	// 		const payload: UserPortfolioColumnOrdering = {
	// 			userPortfolioColumnPreferences: data.map((preference) => ({
	// 				enabled: preference.visible,
	// 				preferencesType: preference.id,
	// 			})),
	// 		};
	// 		await preferencesApi().setUserPortfolioColumnMetricsOrderingPreferences(payload);
	// 		invalidate(bulkAnalysisRepository.columnPreferences.partialQueryKey);
	// 	},
	// ),

	isPresetNameAvailable: newAction((name: string) => axiosExtract(bulkApi().isPresetNameAvailable(name))),
	setFavoritePreset: newAction(async (uuid: string) => {
		await bulkApi().updateFavorite(uuid);
		invalidateEntity(uuid);
	}),
	keyMetric: newQuery(
		"keyMetric",
		(
			metric: NonNullable<PortfolioInsightPanelPreset["metric"]> | null,
			opt?: {
				sortParameters?: MetricParameters;
				sortKey?: FieldKey;
				sortDirection?: Exclude<MetricOrdering, "UNSORTED">;
				filters?: FilterValue[];
				search?: string;
			},
		) => {
			if (!metric || !metric.key) {
				return Promise.resolve(null);
			}
			return axiosExtract(
				bulkApi().getMetricResult({
					key: metric.key,
					referenceEntity: metric.referenceType === "DELTA_RELATIVE" ? metric.referenceEntity : undefined,
					referenceType: metric.referenceType,
					metricParameters: metric.metricParameters,
					sortParameters: opt?.sortParameters,
					sortKey: opt?.sortKey,
					ordering: metric.ordering === "UNSORTED" ? opt?.sortDirection : metric.ordering,
					filters: opt?.filters,
					searchName: opt?.search,
				}),
			);
		},
		{
			staleTime: minutes(5),
			cacheTime: minutes(5),
		},
	),

	bulkAnalysisThresholdForMetric: newQuery("bulkAnalysisThresholdForMetric", (thresholdRequest: ThresholdRequest) =>
		axiosExtract(bulkApi().getThreshold(thresholdRequest)),
	),

	availableEntities: newQuery("availableEntities", () => axiosExtract(bulkApi().getAvailableEntities()), {
		staleTime: 0,
		cacheTime: minutes(10),
	}),
	availableFields: newQuery("availableFields", () => axiosExtract(bulkApi().getAvailableFields()), {
		staleTime: 0,
		cacheTime: minutes(10),
	}),
	presets: newQuery(lists.presets, () => axiosExtract(bulkApi().getPresets()), {
		staleTime: 0,
		cacheTime: days(1),
	}),
	fieldSet: newQuery("fieldSet", (fieldType: FieldType) => axiosExtract(bulkApi().getFieldSet(fieldType)), {
		staleTime: 0,
		cacheTime: minutes(10),
	}),

	riskModelExposureTypes: newQuery("riskModelExposureTypes", () => axiosExtract(riskApi().getRiskModelExposureTypes())),
	savePreset: newAction(async (preset: PresetSaveRequest) => {
		const result = await axiosExtract(bulkApi().savePreset(preset));
		invalidateLists();
		return result;
	}),
	updatePreset: newAction(async (preset: PresetUpdateRequest) => {
		const result = await axiosExtract(bulkApi().updatePreset(preset));
		invalidateEntity(preset.uuid!);
		return result;
	}),
	deletePreset: newAction(async (uuid: string) => {
		await bulkApi().deletePreset(uuid);
		invalidateLists();
	}),
} as const;
